import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';

import type { AdminProject } from 'features/projects';
import { Play } from 'lucide-react';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { useDeleteScenarioMutation, useGetScenariosQuery, useUpdateScenarioMutation } from '../../api/scenarios';
import type { AgentTestScenario } from '../../model/scenario';
import { categoriesFromScenarios } from '../../model/scenario';
import ScenarioCards from './ScenarioCards';
import ScenarioConfirmDialog from './ScenarioConfirmDialog';
import ScenarioDuplicateDialog from './ScenarioDuplicateDialog';
import ScenarioFormDialog from './ScenarioFormDialog';
import ScenarioPreviewPanel from './ScenarioPreviewPanel';
import ScenariosPagination from './ScenariosPagination';
import ScenariosToolbar, { type EnabledFilter, type ScopeFilter } from './ScenariosToolbar';

const PAGE_SIZE = 20;
const TAG_DEBOUNCE_MS = 300;
const ACTION_ERROR_FALLBACK = 'No se pudo completar la acción. Intenta nuevamente.';

type FormDialogState = { mode: 'create' } | { mode: 'edit'; scenario: AgentTestScenario };
type ConfirmState = { kind: 'delete' | 'toggle'; scenario: AgentTestScenario };

interface ScenariosSectionProps {
  masterProject: { id: number; name: string };
  subProjects: AdminProject[];
}

const ScenariosSection = ({ masterProject, subProjects }: ScenariosSectionProps): JSX.Element => {
  const [scope, setScope] = useState<ScopeFilter>('all');
  const [enabled, setEnabled] = useState<EnabledFilter>('all');
  const [tagInput, setTagInput] = useState('');
  const [tag, setTag] = useState('');
  const [page, setPage] = useState(1);

  const [formDialog, setFormDialog] = useState<FormDialogState | null>(null);
  const [duplicateTarget, setDuplicateTarget] = useState<AgentTestScenario | null>(null);
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<number>>(new Set());
  const [previewedId, setPreviewedId] = useState<number | null>(null);

  useEffect(() => {
    const handle = setTimeout(() => {
      setTag(tagInput.trim());
    }, TAG_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [tagInput]);

  useEffect(() => {
    setPage(1);
  }, [scope, enabled, tag, masterProject.id]);

  const { data, isLoading, isFetching, isError, refetch } = useGetScenariosQuery({
    projectId: masterProject.id,
    scope: scope === 'all' ? undefined : scope,
    enabled: enabled === 'all' ? undefined : enabled === 'enabled',
    tag: tag.length > 0 ? tag : undefined,
    page,
    limit: PAGE_SIZE,
  });

  const scenarios = data?.data ?? [];
  const pagination = data?.pagination;

  const { data: categoriesSource } = useGetScenariosQuery({
    projectId: masterProject.id,
    page: 1,
    limit: 100,
  });
  const categories = useMemo(() => categoriesFromScenarios(categoriesSource?.data ?? []), [categoriesSource]);

  const subProjectNames = useMemo(
    () => Object.fromEntries(subProjects.map((sub) => [sub.id, sub.name])) as Record<number, string>,
    [subProjects]
  );

  const [updateScenario, { isLoading: isToggling }] = useUpdateScenarioMutation();
  const [deleteScenario, { isLoading: isDeleting }] = useDeleteScenarioMutation();

  const previewedScenario = scenarios.find((scenario) => scenario.id === previewedId) ?? null;

  const handleToggleSelected = (scenario: AgentTestScenario, selected: boolean): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) next.add(scenario.id);
      else next.delete(scenario.id);
      return next;
    });
  };

  const handleToggleSection = (sectionScenarios: AgentTestScenario[], selected: boolean): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const scenario of sectionScenarios) {
        if (selected) next.add(scenario.id);
        else next.delete(scenario.id);
      }
      return next;
    });
  };

  const runToggle = async (scenario: AgentTestScenario): Promise<void> => {
    setActionError(null);
    try {
      await updateScenario({ scenarioId: scenario.id, body: { enabled: !scenario.enabled } }).unwrap();
      setConfirmState(null);
    } catch (err) {
      setConfirmState(null);
      setActionError(extractErrorMessage(err, ACTION_ERROR_FALLBACK));
    }
  };

  const runDelete = async (scenario: AgentTestScenario): Promise<void> => {
    setActionError(null);
    try {
      await deleteScenario(scenario.id).unwrap();
      setConfirmState(null);
    } catch (err) {
      setConfirmState(null);
      setActionError(extractErrorMessage(err, ACTION_ERROR_FALLBACK));
    }
  };

  const handleToggleEnabled = (scenario: AgentTestScenario): void => {
    if (scenario.scope === 'global') {
      setConfirmState({ kind: 'toggle', scenario });
      return;
    }
    void runToggle(scenario);
  };

  const handleDelete = (scenario: AgentTestScenario): void => {
    setConfirmState({ kind: 'delete', scenario });
  };

  const confirmDialogProps = useMemo(() => {
    if (!confirmState) return null;
    const { kind, scenario } = confirmState;
    const globalWarning =
      scenario.scope === 'global'
        ? ' Este escenario es global: el cambio afecta los tests de todas las inmobiliarias.'
        : '';

    if (kind === 'delete') {
      return {
        title: 'Eliminar escenario',
        description: `Vas a eliminar «${scenario.name}».${globalWarning} Esta acción no se puede deshacer desde aquí.`,
        confirmLabel: 'Eliminar',
        destructive: true,
        onConfirm: (): void => {
          void runDelete(scenario);
        },
      };
    }
    return {
      title: scenario.enabled ? 'Deshabilitar escenario global' : 'Habilitar escenario global',
      description: `Vas a ${scenario.enabled ? 'deshabilitar' : 'habilitar'} «${scenario.name}».${globalWarning}`,
      confirmLabel: scenario.enabled ? 'Deshabilitar' : 'Habilitar',
      destructive: false,
      onConfirm: (): void => {
        void runToggle(scenario);
      },
    };
  }, [confirmState]);

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-4">
      <ScenariosToolbar
        scope={scope}
        enabled={enabled}
        tagInput={tagInput}
        categories={categories}
        onScopeChange={setScope}
        onEnabledChange={setEnabled}
        onTagInputChange={setTagInput}
        onCreate={() => setFormDialog({ mode: 'create' })}
      />

      {actionError ? (
        <div role="alert" className="rounded-md border border-error/30 bg-error-light px-3 py-2 text-sm text-error">
          {actionError}
        </div>
      ) : null}

      {isLoading ? (
        <div className="flex items-center justify-center rounded-xl border border-border-default bg-bg-surface-alt py-14">
          <p className="m-0 text-sm text-text-muted font-body">Cargando escenarios…</p>
        </div>
      ) : null}

      {isError && !isLoading ? (
        <div
          role="alert"
          className="flex flex-col items-center gap-3 rounded-xl border border-error/30 bg-error-light px-6 py-10 text-center"
        >
          <p className="m-0 text-sm text-error font-body">No se pudieron cargar los escenarios.</p>
          <Button type="button" variant="outline" onClick={() => void refetch()}>
            Reintentar
          </Button>
        </div>
      ) : null}

      {!isLoading && !isError && scenarios.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border-default
            bg-bg-surface-alt px-6 py-14 text-center"
          role="status"
        >
          <p className="m-0 max-w-md text-sm text-text-muted font-body">
            {scope !== 'all' || enabled !== 'all' || tag.length > 0
              ? 'No hay escenarios que coincidan con los filtros.'
              : 'Aún no hay escenarios para esta inmobiliaria. Crea el primero.'}
          </p>
          <Button type="button" onClick={() => setFormDialog({ mode: 'create' })}>
            Nuevo escenario
          </Button>
        </div>
      ) : null}

      {!isLoading && !isError && scenarios.length > 0 ? (
        <>
          <div className="grid min-h-0 flex-1 items-stretch gap-4 xl:grid-cols-2">
            <div className="flex h-full min-h-0 flex-col gap-4">
              <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                <ScenarioCards
                  scenarios={scenarios}
                  groupBySections={scope === 'all'}
                  subProjectNames={subProjectNames}
                  selectedIds={selectedIds}
                  previewedId={previewedId}
                  onToggleSelected={handleToggleSelected}
                  onToggleSection={handleToggleSection}
                  onPreview={(scenario) => setPreviewedId(scenario.id)}
                  onEdit={(scenario) => setFormDialog({ mode: 'edit', scenario })}
                  onToggleEnabled={handleToggleEnabled}
                  onDuplicate={setDuplicateTarget}
                  onDelete={handleDelete}
                />
              </div>
              {pagination && pagination.totalPages > 1 ? (
                <div className="shrink-0">
                  <ScenariosPagination
                    page={pagination.page}
                    limit={pagination.limit}
                    total={pagination.total}
                    totalPages={pagination.totalPages}
                    isFetching={isFetching}
                    onPageChange={setPage}
                  />
                </div>
              ) : null}
            </div>
            <div className="mb-20 min-h-0">
              <ScenarioPreviewPanel scenario={previewedScenario} subProjectNames={subProjectNames} />
            </div>
          </div>

          {selectedIds.size > 0 ? (
            <div
              className="fixed bottom-4 left-[calc(50%+var(--sidebar-width)/2)] z-40 flex
                w-[calc((100%-var(--sidebar-width))*0.95)] -translate-x-1/2 items-center justify-between gap-3
                rounded-xl border border-border-subtle bg-bg-surface px-4 py-3 shadow-lg"
            >
              <span className="text-sm text-text-muted">
                {selectedIds.size === 1 ? '1 escenario seleccionado' : `${selectedIds.size} escenarios seleccionados`}
              </span>
              <div className="flex items-center gap-3">
                <Button type="button" variant="ghost" onClick={() => setSelectedIds(new Set())}>
                  Limpiar selección
                </Button>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button type="button" disabled>
                        <Play className="size-4" aria-hidden />
                        Correr tests
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">Disponible cuando estén listos los runs de tests.</TooltipContent>
                </Tooltip>
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      <ScenarioFormDialog
        mode={formDialog?.mode ?? 'create'}
        scenario={formDialog?.mode === 'edit' ? formDialog.scenario : null}
        masterProject={masterProject}
        subProjects={subProjects}
        categories={categories}
        open={formDialog !== null}
        onOpenChange={(open) => {
          if (!open) setFormDialog(null);
        }}
      />

      <ScenarioDuplicateDialog
        scenario={duplicateTarget}
        defaultProjectId={masterProject.id}
        open={duplicateTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDuplicateTarget(null);
        }}
      />

      {confirmDialogProps ? (
        <ScenarioConfirmDialog
          open
          title={confirmDialogProps.title}
          description={confirmDialogProps.description}
          confirmLabel={confirmDialogProps.confirmLabel}
          destructive={confirmDialogProps.destructive}
          isLoading={isToggling || isDeleting}
          onCancel={() => setConfirmState(null)}
          onConfirm={confirmDialogProps.onConfirm}
        />
      ) : null}
    </section>
  );
};

export default ScenariosSection;
